import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { LoadingService } from '../../../services/loading.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { SimulatorType } from '../../../core/enums/simulator-type.enum';
import { CubeSizeResult, RunSearchFilters, SimulationSearchFilters } from '../../../core/models/simulation.model';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { FilterOptionsPopupComponent } from '../filter-options-popup/filter-options-popup.component';
import { filter } from 'rxjs/operators';
import { RemoveExtensionPipe } from '../../pipes/remove-extension.pipe';
import { sanitizeSearchString } from '../../../utils/sanitize.util';
import { toScientificNotation } from '../../../utils/format.util';

export type PermeabilityPopupMode = 'cubeTrend' | 'subsample';

interface SimulationRow {
  simulationId: string;
  simulationName: string;
  fileName: string;
  results: Array<{
    direction: string;
    runId: string;
    cubeSizeResults: CubeSizeResult[];
    connectedPorosity?: number | null;
    absolutePermeability?: number | null;
  }>;
  selectedDirection: string | null;
  selectedCubeSize: CubeSizeResult | null;
  angularCoefficient: number | null;
  exponentialCoefficient: number | null;
  connectedPorosity: number | null;
  absolutePermeability: number | null;
}

@Component({
  selector: 'app-select-permeability-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatRadioModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './select-permeability-popup.component.html',
  styleUrl: './select-permeability-popup.component.scss',
})
export class SelectPermeabilityPopupComponent extends Destroyable(Object) implements OnInit {
  private readonly componentId: string = 'select-permeability-popup';
  readonly toScientificNotation = toScientificNotation;
  isLoading = false;
  simulations: SimulationRow[] = [];
  selectedSimulation: SimulationRow | null = null;
  private simulationResultsCache = new Map<string, any[]>();

  readonly searchByOptions: { label: string; value: string }[] = [
    { label: 'Name', value: 'Name' },
    { label: 'Rock file', value: 'File' },
  ];
  filterByOptionSelected: string = 'File';
  searchString: string = '';
  filterData: RunSearchFilters | null = null;
  readonly runStatuses = ['Success', 'Failure', 'Waiting', 'Initializing', 'Processing', 'Canceled'];

  displayedColumns: string[] = [];
  mode: PermeabilityPopupMode = 'cubeTrend';

  get title(): string {
    return this.mode === 'subsample'
      ? 'Subsample Absolute Permeability'
      : 'Cube Trend - Absolute Permeability';
  }

  get emptyMessage(): string {
    return this.mode === 'subsample'
      ? 'No subsample permeability simulations found.'
      : 'No cube trend simulations found for this rock file.';
  }

  get isConfirmDisabled(): boolean {
    if (!this.selectedSimulation || !this.selectedSimulation.selectedDirection) return true;
    if (this.mode === 'cubeTrend' && !this.selectedSimulation.selectedCubeSize) return true;
    return false;
  }

  private preselection: {
    simulationId: string;
    direction: string;
    cubeSizeVoxels?: number;
  } | null = null;

  constructor(
    public readonly dialogRef: MatDialogRef<SelectPermeabilityPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      fileName: string;
      mode?: PermeabilityPopupMode;
      preselection?: { simulationId: string; direction: string; cubeSizeVoxels?: number };
    },
    private readonly simulationsService: SimulationsService,
    private readonly loadingService: LoadingService,
    private readonly snackbarService: SnackbarService,
    private readonly dialog: MatDialog,
  ) {
    super();
    this.mode = data.mode || 'cubeTrend';
    this.preselection = data.preselection || null;
    this.displayedColumns = this.mode === 'subsample'
      ? ['select', 'simulationName', 'rockFile', 'direction', 'connectedPorosity', 'absolutePermeability']
      : ['select', 'simulationName', 'rockFile', 'direction', 'cubeSize', 'angularCoefficient', 'exponentialCoefficient'];
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.loadSimulations();
  }

  onSelect(): void {
    if (!this.selectedSimulation || !this.selectedSimulation.selectedDirection) return;

    if (this.mode === 'subsample') {
      this.dialogRef.close({
        simulationId: this.selectedSimulation.simulationId,
        simulationName: this.selectedSimulation.simulationName,
        direction: this.selectedSimulation.selectedDirection,
        connectedPorosity: this.selectedSimulation.connectedPorosity,
        absolutePermeability: this.selectedSimulation.absolutePermeability,
      });
    } else {
      if (!this.selectedSimulation.selectedCubeSize) return;
      this.dialogRef.close({
        simulationId: this.selectedSimulation.simulationId,
        simulationName: this.selectedSimulation.simulationName,
        direction: this.selectedSimulation.selectedDirection,
        cubeSize: this.selectedSimulation.selectedCubeSize.cubeSizeVoxels,
        cubeSizeVoxels: this.selectedSimulation.selectedCubeSize.cubeSizeVoxels,
        cubeSizeFraction: this.selectedSimulation.selectedCubeSize.cubeSizeFraction,
        angularCoefficient: this.selectedSimulation.angularCoefficient!,
        exponentialCoefficient: this.selectedSimulation.exponentialCoefficient!,
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  selectSimulation(simulation: SimulationRow): void {
    // Clear previous row's selection state
    if (this.selectedSimulation && this.selectedSimulation !== simulation) {
      const prev = this.selectedSimulation;
      prev.selectedDirection = null;
      prev.selectedCubeSize = null;
      prev.angularCoefficient = null;
      prev.exponentialCoefficient = null;
      prev.connectedPorosity = null;
      prev.absolutePermeability = null;
    }

    this.selectedSimulation = simulation;

    if (this.mode !== 'subsample') return;

    // Already cached — just apply if direction already selected
    if (this.simulationResultsCache.has(simulation.simulationId)) {
      if (simulation.selectedDirection) {
        this.applySubsampleValuesFromCache(simulation);
      }
      return;
    }

    // Fetch and cache the simulation detail
    this.simulationsService
      .getSimulationById(simulation.simulationId)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (simDetail) => {
          this.simulationResultsCache.set(simulation.simulationId, simDetail.results ?? []);
          if (simulation.selectedDirection) {
            this.applySubsampleValuesFromCache(simulation);
          }
        },
      });
  }

  onDirectionChange(simulation: SimulationRow): void {
    if (!simulation.selectedDirection) {
      simulation.selectedCubeSize = null;
      simulation.angularCoefficient = null;
      simulation.exponentialCoefficient = null;
      simulation.connectedPorosity = null;
      simulation.absolutePermeability = null;
      return;
    }
    simulation.selectedCubeSize = null;
    simulation.angularCoefficient = null;
    simulation.exponentialCoefficient = null;
    simulation.connectedPorosity = null;
    simulation.absolutePermeability = null;

    if (this.mode === 'subsample') {
      this.applySubsampleValuesFromCache(simulation);
    } else {
      this.updateCoefficients(simulation);
    }
  }

  onCubeSizeChange(simulation: SimulationRow): void {
    this.updateCoefficients(simulation);
  }

  private applySubsampleValuesFromCache(simulation: SimulationRow): void {
    if (!simulation.selectedDirection) return;

    const cachedResults = this.simulationResultsCache.get(simulation.simulationId) ?? [];
    const match = cachedResults.find(
      (r: any) => r.direction?.toLowerCase() === simulation.selectedDirection!.toLowerCase(),
    );

    simulation.connectedPorosity = match?.result?.connectedPorosity ?? null;
    simulation.absolutePermeability = match?.result?.absolutePermeability ?? null;
  }

  private updateCoefficients(simulation: SimulationRow): void {
    if (!simulation.selectedDirection) {
      simulation.angularCoefficient = null;
      simulation.exponentialCoefficient = null;
      return;
    }

    const selectedResult = simulation.results.find(
      (r) => r.direction.toLowerCase() === simulation.selectedDirection!.toLowerCase(),
    );

    if (selectedResult && simulation.selectedCubeSize) {
      const cubeSizeResult = selectedResult.cubeSizeResults.find(
        (cs) => cs.cubeSizeVoxels === simulation.selectedCubeSize!.cubeSizeVoxels,
      );
      if (cubeSizeResult) {
        simulation.angularCoefficient = cubeSizeResult.angularCoefficient;
        simulation.exponentialCoefficient = cubeSizeResult.exponentialCoefficient;
      }
    }
  }

  getAvailableDirections(simulation: SimulationRow): string[] {
    return simulation.results.map((r) => r.direction.toUpperCase());
  }

  isDirectionInvalid(simulation: SimulationRow, direction: string): boolean {
    const cachedResults = this.simulationResultsCache.get(simulation.simulationId) ?? [];
    const match = cachedResults.find(
      (r: any) => r.direction?.toLowerCase() === direction.toLowerCase(),
    );
    if (!match) return false;
    const porosity = match.result?.connectedPorosity ?? null;
    const permeability = match.result?.absolutePermeability ?? null;
    return (porosity !== null && porosity <= 0) || (permeability !== null && permeability <= 0);
  }

  getAvailableCubeSizes(simulation: SimulationRow): CubeSizeResult[] {
    if (!simulation.selectedDirection) {
      return [];
    }
    const selectedResult = simulation.results.find(
      (r) => r.direction.toLowerCase() === simulation.selectedDirection!.toLowerCase(),
    );
    return selectedResult?.cubeSizeResults || [];
  }

  getCubeSizeDisplay(cubeSize: CubeSizeResult): string {
    return `${cubeSize.cubeSizeVoxels} (${cubeSize.cubeSizeFraction}%)`;
  }

  formatPermeability(value: number): string {
    return value.toFixed(2);
  }

  formatPorosity(value: number): string {
    return (value * 100).toFixed(2);
  }

  compareCubeSize(a: CubeSizeResult | null, b: CubeSizeResult | null): boolean {
    if (!a || !b) return a === b;
    return a.cubeSizeVoxels === b.cubeSizeVoxels && a.cubeSizeFraction === b.cubeSizeFraction;
  }

  onSearch(): void {
    this.loadSimulations();
  }

  onClearSearch(): void {
    this.searchString = '';
    this.onSearch();
  }

  onSearchStringChange(): void {
    if (!this.searchString) {
      this.onClearSearch();
    }
  }

  openFilterPopup(): void {
    const dialogRef = this.dialog.open(FilterOptionsPopupComponent, {
      disableClose: true,
      data: {
        ...this.filterData,
        statuses: this.runStatuses,
        hideTypes: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        this.takeUntilDestroyed(),
      )
      .subscribe((data: SimulationSearchFilters) => {
        const directions = [];
        if (data.directions?.['x']) directions.push('x');
        if (data.directions?.['y']) directions.push('y');
        if (data.directions?.['z']) directions.push('z');

        if (this.filterData) {
          this.filterData.direction = directions;
        }
        this.loadSimulations();
      });
  }

  private loadSimulations(): void {
    this.loadingService.startLoading(this.componentId);

    const filterData: RunSearchFilters = {
      filterBy: this.filterByOptionSelected,
      searchString: sanitizeSearchString(this.searchString),
      type: this.mode === 'subsample' ? SimulatorType.SubsampleKabs : SimulatorType.SubsampleCubeTrendKabs,
      direction: this.filterData?.direction || [],
      cycles: this.filterData?.cycles || [],
      interfacialTensionMin: this.filterData?.interfacialTensionMin || null,
      interfacialTensionMax: this.filterData?.interfacialTensionMax || null,
      primaryDrainageMin: this.filterData?.primaryDrainageMin || null,
      primaryDrainageMax: this.filterData?.primaryDrainageMax || null,
      firstImbibitionMin: this.filterData?.firstImbibitionMin || null,
      firstImbibitionMax: this.filterData?.firstImbibitionMax || null,
      sortColumnName: this.filterData?.sortColumnName || null,
      sortDirection: this.filterData?.sortDirection || null,
      page: 1,
      pageSize: 100,
    };

    this.filterData = filterData;

    this.simulationsService
      .getFilteredSimulations(filterData, this.mode !== 'subsample')
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (result) => {
          this.processSimulations(result.data);
          this.applyPreselection();
          this.loadingService.stopLoading(this.componentId);
        },
        error: () => {
          this.loadingService.stopLoading(this.componentId);
        },
      });
  }

  private applyPreselection(): void {
    if (!this.preselection) return;

    const match = this.simulations.find(
      (s) => s.simulationId === this.preselection!.simulationId,
    );
    if (!match) return;

    this.selectedSimulation = match;
    match.selectedDirection = match.results.find(
      (r) => r.direction.toLowerCase() === this.preselection!.direction.toLowerCase(),
    )?.direction.toUpperCase() || null;

    if (match.selectedDirection) {
      if (this.mode === 'subsample') {
        this.selectSimulation(match);
      } else if (this.preselection.cubeSizeVoxels != null) {
        const cubeSizes = this.getAvailableCubeSizes(match);
        match.selectedCubeSize = cubeSizes.find(
          (cs) => cs.cubeSizeVoxels === this.preselection!.cubeSizeVoxels,
        ) || null;
        this.updateCoefficients(match);
      }
    }

    this.preselection = null;
  }

  private processSimulations(data: any[]): void {
    this.simulations = [];
    this.simulationResultsCache.clear();

    if (this.mode === 'subsample') {
      this.processSubsampleSimulations(data);
    } else {
      this.processCubeTrendSimulations(data);
    }
  }

  private processSubsampleSimulations(data: any[]): void {
    data.forEach((simulation: any) => {
      const results: SimulationRow['results'] = [];

      if (simulation.results && Array.isArray(simulation.results)) {
        simulation.results.forEach((result: any) => {
          results.push({
            direction: result.direction,
            runId: result.runId,
            cubeSizeResults: [],
            connectedPorosity: result.result?.connectedPorosity ?? null,
            absolutePermeability: result.result?.absolutePermeability ?? null,
          });
        });
      }

      if (results.length > 0) {
        this.simulations.push({
          simulationId: simulation.id,
          simulationName: simulation.name,
          fileName: simulation.fileName,
          results,
          selectedDirection: null,
          selectedCubeSize: null,
          angularCoefficient: null,
          exponentialCoefficient: null,
          connectedPorosity: null,
          absolutePermeability: null,
        });
      }
    });
  }

  private processCubeTrendSimulations(data: any[]): void {
    data.forEach((simulation: any) => {
      const results: SimulationRow['results'] = [];

      if (simulation.results && Array.isArray(simulation.results)) {
        simulation.results.forEach((result: any) => {
          if (result.result && result.result.cubeSizeResults && Array.isArray(result.result.cubeSizeResults)) {
            const validCubeSizes = result.result.cubeSizeResults.filter(
              (cs: any) =>
                cs !== null &&
                cs.angularCoefficient !== undefined &&
                cs.exponentialCoefficient !== undefined,
            );
            if (validCubeSizes.length > 0) {
              results.push({
                direction: result.direction,
                runId: result.runId,
                cubeSizeResults: validCubeSizes,
              });
            }
          }
        });
      }

      if (results.length > 0) {
        this.simulations.push({
          simulationId: simulation.id,
          simulationName: simulation.name,
          fileName: simulation.fileName,
          results,
          selectedDirection: null,
          selectedCubeSize: null,
          angularCoefficient: null,
          exponentialCoefficient: null,
          connectedPorosity: null,
          absolutePermeability: null,
        });
      }
    });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}